// PreviewTester/utils/renderingUtils.js

/**
 * Shared rendering utilities - Vanilla JS (no React dependencies)
 * These functions are pure logic and can be used in both React and Vanilla JS
 */

import {
  createMinimalTransformationStep,
  CLASS_MAP,
  generateTableHTML,
  generateCardHTML,
  generateListHTML,
  generatePositionSelector,
  parseSelectedText,
  getLoopContext,
} from "./transformationRules";

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Check if content contains HTML
 */
export const hasHtml = (content) => {
  return /<[a-z][\s\S]*>/i.test(content);
};


export const applyMediaToAllInLoop = (
  selector,
  selectedText,
  htmlString,
  containerElement,
  options = {}
) => {
  console.log("[applyMediaToAllInLoop] ========================================");
  console.log("[applyMediaToAllInLoop] 🚀 START");
  console.log("[applyMediaToAllInLoop] selector:", JSON.stringify(selector));
  console.log("[applyMediaToAllInLoop] options:", options);

  const { width = 250, height = 250, preserveAspectRatio = true } = options;

  if (!selector || !selector.loopSelector) {
    return { success: false, error: "No loop selector found" };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  // Find all screens.
  const allScreens = Array.from(doc.body.querySelectorAll(".item-ui-screen"));
  if (allScreens.length === 0) {
    const contentWrapsList = Array.from(doc.body.querySelectorAll(".contentWraps"));
    for (const wrap of contentWrapsList) {
      const wrapper = doc.createElement("div");
      wrapper.className = "item-ui-screen";
      wrapper.setAttribute("data-item-ui-index", "0");
      const parent = wrap.parentNode;
      parent.replaceChild(wrapper, wrap);
      wrapper.appendChild(wrap);
      allScreens.push(wrapper);
    }
  }
  if (allScreens.length === 0) {
    console.log("[applyMediaToAllInLoop] ❌ No screens found");
    return { success: false, error: "No screens found" };
  }

  const relativePath =
    typeof selector.start === "string"
      ? selector.start
      : selector.start?.elementPath || "";
  if (!relativePath) {
    console.log("[applyMediaToAllInLoop] ❌ No relative path found. selector.start =", selector.start);
    return { success: false, error: "No relative path found" };
  }

  const createImg = (url) => {
    const cleanUrl = url.replace(/"/g, "&quot;");
    const objectFit = preserveAspectRatio ? "cover" : "fill";
    return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;"><img src="${cleanUrl}" alt="Image" class="preview-image rendered-image" style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" loading="lazy"/></div>`;
  };

  let replacedCount = 0;
  let screensTouched = 0;
  let firstFailureReason = null;

  for (let sIdx = 0; sIdx < allScreens.length; sIdx++) {
    const screen = allScreens[sIdx];
    console.log(`[applyMediaToAllInLoop] 🖥️ Screen ${sIdx} (${sIdx + 1}/${allScreens.length})`);

    // ----------------------------------------------------------
    // ✅ CLEAN RESOLUTION: loopSelector is screen-rooted. Direct lookup.
    // ----------------------------------------------------------
    let loopContainer = findElementByPath(screen, selector.loopSelector);
    if (!loopContainer || !loopContainer.classList.contains("for-loop-started")) {
      console.log(`[applyMediaToAllInLoop] ⚠️ Screen ${sIdx}: direct resolution failed, trying querySelector`);
      loopContainer = screen.querySelector(".for-loop-started");
    }
    if (!loopContainer) {
      console.log(`[applyMediaToAllInLoop] ⏭️ Screen ${sIdx}: loop container not found, skipping`);
      if (!firstFailureReason) firstFailureReason = "Loop container not found in one or more screens";
      continue;
    }

    const iterationGroups = getIterationGroups(loopContainer);
    console.log(`[applyMediaToAllInLoop] Screen ${sIdx} iterationGroups:`, iterationGroups ? `${iterationGroups.length} groups` : "null");

    // ----------------------------------------------------------
    // Wrapped loop path
    // ----------------------------------------------------------
    if (iterationGroups) {
      const relParts = relativePath.split(" > ");
      const partLooksLikeDiv = (p) => /^div:nth-child\(\d+\)$/.test(p);

      let rowIndexInIter = -1;
      let childPath = "";
      let shape = "unknown";

      // Shape A: wrapper-anchored
      if (relParts.length >= 2 && partLooksLikeDiv(relParts[0])) {
        const probeEl = findElementByPath(loopContainer, relParts[0]);
        if (probeEl && probeEl.hasAttribute && probeEl.hasAttribute("data-iter-id")) {
          const rowMatch = relParts[1].match(/div:nth-child\((\d+)\)/);
          if (rowMatch) {
            rowIndexInIter = parseInt(rowMatch[1], 10) - 1;
            childPath = relParts.slice(2).join(" > ");
            shape = "wrapper-anchored";
          }
        }
      }

      // Shape B: row-anchored
      if (rowIndexInIter === -1 && relParts.length >= 1 && partLooksLikeDiv(relParts[0])) {
        const rowMatch = relParts[0].match(/div:nth-child\((\d+)\)/);
        if (rowMatch) {
          rowIndexInIter = parseInt(rowMatch[1], 10) - 1;
          childPath = relParts.slice(1).join(" > ");
          shape = "row-anchored";
        }
      }

      // ============================================================
      // ✅ DEV-ONLY GUARD — 1E
      // INVARIANT: every selector arriving at applyMediaToAllInLoop
      //            must be classifiable as wrapper-anchored or
      //            row-anchored — never "unknown".
      //
      // Reason: an "unknown" shape means the path does not match
      // either of the two sanctioned shapes. The image-replacement
      // step will silently skip the row, and the user will see
      // no images converted.
      //
      // Owned by: getRelativePath / getNthChildPath in renderingUtils.js
      //           + createDeepSelectors in PreviewTester.js
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        if (shape === "unknown") {
          console.warn(
            "[INVARIANT:1E] applyMediaToAllInLoop could not classify path shape.",
            {
              screenIndex: sIdx,
              relativePath,
              relParts,
              loopSelector: selector.loopSelector,
              loopContainerClass: loopContainer.className,
            },
          );
        }
      }

      if (rowIndexInIter === -1) {
        console.log(`[applyMediaToAllInLoop] ⚠️ Screen ${sIdx}: cannot resolve row from path: ${relativePath}`);
        if (!firstFailureReason) firstFailureReason = "Cannot resolve iteration row from path";
        continue;
      }

      let screenReplacedCount = 0;

      for (let g = 0; g < iterationGroups.length; g++) {
        const group = iterationGroups[g];
        const targetRow = group.rows[rowIndexInIter];
        if (!targetRow) {
          console.log(`[applyMediaToAllInLoop] ⚠️ Screen ${sIdx}: no row ${rowIndexInIter} in iter ${g}`);
          continue;
        }

        let imageElement = null;
        if (childPath) {
          imageElement = findElementByPath(targetRow, childPath);
        }
        if (!imageElement || !(
          imageElement.classList?.contains("image-url") ||
          imageElement.getAttribute?.("data-image-url")
        )) {
          const inner = targetRow.querySelector(".value.image-url, [data-image-url]");
          if (inner) imageElement = inner;
        }
        if (!imageElement) {
          console.log(`[applyMediaToAllInLoop] ⚠️ Screen ${sIdx}: no image-url element in iter=${g}, row=${rowIndexInIter}`);
          continue;
        }

        const imageUrl =
          imageElement.getAttribute?.("data-image-url") ||
          imageElement.textContent?.trim() ||
          "";
        if (!imageUrl || !isImageUrl(imageUrl)) continue;

        const tempDiv = doc.createElement("div");
        tempDiv.innerHTML = createImg(imageUrl);
        const newElement = tempDiv.firstChild;
        if (newElement && imageElement.parentNode) {
          imageElement.parentNode.replaceChild(newElement, imageElement);
          replacedCount++;
          screenReplacedCount++;
        }
      }

      if (screenReplacedCount > 0) screensTouched++;
      continue;
    }

    // ----------------------------------------------------------
    // Legacy path
    // ----------------------------------------------------------
    console.log(`[applyMediaToAllInLoop] Screen ${sIdx} 🕰️ LEGACY path`);

    let itemsPerIteration =
      parseInt(loopContainer.getAttribute("data-items-per-iteration")) || 0;
    if (!itemsPerIteration) itemsPerIteration = 4;

    const allRowsArray = Array.from(loopContainer.querySelectorAll(".row"));

    const targetElement = findElementByPath(loopContainer, relativePath);
    if (!targetElement) {
      if (!firstFailureReason) firstFailureReason = "Could not find target row";
      continue;
    }

    let targetRowIndex = -1;
    for (let i = 0; i < allRowsArray.length; i++) {
      if (allRowsArray[i] === targetElement || allRowsArray[i].contains(targetElement)) {
        targetRowIndex = i;
        break;
      }
    }
    if (targetRowIndex === -1) {
      if (!firstFailureReason) firstFailureReason = "Could not find target row";
      continue;
    }

    const targetPosition = targetRowIndex % itemsPerIteration;

    let screenReplacedCount = 0;
    for (let i = 0; i < allRowsArray.length; i++) {
      if (i % itemsPerIteration !== targetPosition) continue;
      const row = allRowsArray[i];
      const imageElement = findImageUrlElement(row, relativePath);
      if (!imageElement) continue;

      const imageUrl = imageElement.textContent?.trim() || "";
      if (!imageUrl || !isImageUrl(imageUrl)) continue;

      const tempDiv = doc.createElement("div");
      tempDiv.innerHTML = createImg(imageUrl);
      const newElement = tempDiv.firstChild;
      if (newElement) {
        imageElement.parentNode.replaceChild(newElement, imageElement);
        replacedCount++;
        screenReplacedCount++;
      }
    }

    if (screenReplacedCount > 0) screensTouched++;
  }

  if (replacedCount === 0) {
    console.log("[applyMediaToAllInLoop] ⚠️ No images replaced");
    return { success: false, error: firstFailureReason || "No images replaced" };
  }

  let serialized = "";
  for (const child of doc.body.children) serialized += child.outerHTML;

  console.log(`[applyMediaToAllInLoop] ✅ Replaced ${replacedCount} images across ${screensTouched} screen(s)`);
  return { success: true, newPreview: serialized, replacedCount };
};


/**
 * Helper: Find the image URL element within a row using a relative path
 */
const findImageUrlElement = (row, relativePath) => {
  if (!row || !relativePath) return null;

  // Try to find by class first (tag agnostic)
  const imageElement = row.querySelector(".value.image-url");
  if (imageElement) return imageElement;

  // Try by path
  const element = findElementByPath(row, relativePath);
  if (
    element &&
    (element.classList?.contains("image-url") ||
      element.getAttribute("data-image-url"))
  ) {
    return element;
  }

  // Try to find any element with data-image-url (tag agnostic)
  const dataImageElement = row.querySelector("[data-image-url]");
  if (dataImageElement) return dataImageElement;

  return null;
};

/**
 * Helper: Create image HTML
 */
// PreviewTester/utils/renderingUtils.js - Updated createImageHtml with custom dimensions

/**
 * Helper: Create image HTML with custom dimensions
 * @param {string} url - The image URL
 * @param {number} width - Image width in pixels (default: 250)
 * @param {number} height - Image height in pixels (default: 250)
 * @param {boolean} preserveAspectRatio - Whether to preserve aspect ratio (default: true)
 */
export const createImageHtml = (url, width = 250, height = 250, preserveAspectRatio = true) => {
  const cleanUrl = url.replace(/"/g, "&quot;");

  const objectFit = preserveAspectRatio ? 'cover' : 'fill';

  return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;">
    <img 
      src="${cleanUrl}" 
      alt="Image" 
      class="preview-image rendered-image" 
      style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" 
      loading="lazy" 
      onerror="this.style.display='none';this.parentElement.innerHTML='<div style=\\'color:#a0aec0;font-size:12px;\\'>🖼️ Image unavailable</div>';"
    />
  </div>`;
};

export const parseRowsToData = (rowContent) => {
  console.log("[parseRowsToData] START", { rowContent });

  if (!rowContent) {
    return null;
  }

  const lines = rowContent
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  console.log("[parseRowsToData] 📊 Parsed lines", { lines });

  if (lines.length === 0) {
    return null;
  }

  const items = [];
  let currentItem = {};
  let itemStarterKey = null;
  const keyCounts = {};

  // FIRST PASS: Detect item boundaries by counting key occurrences
  for (const line of lines) {
    let key = null;

    let match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*/);
    if (match) {
      key = match[1];
    } else {
      match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[,;]\s*/);
      if (match) {
        key = match[1];
      } else {
        match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s+-\s+/);
        if (match) {
          key = match[1];
        }
      }
    }

    if (key) {
      keyCounts[key] = (keyCounts[key] || 0) + 1;
    }
  }

  let maxCount = 0;
  for (const [key, count] of Object.entries(keyCounts)) {
    if (count > maxCount) {
      maxCount = count;
      itemStarterKey = key;
    }
  }

  console.log("[parseRowsToData] 📊 First pass completed", {
    itemStarterKey,
    keyCounts,
    maxCount,
  });

  // SECOND PASS: Build items
  for (const line of lines) {
    let key = null;
    let value = null;

    let match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.*)$/);
    if (!match) {
      match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[,;]\s*(.*)$/);
    }
    if (!match) {
      match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s+-\s+(.*)$/);
    }
    if (!match) {
      match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*:\s*(.*)$/);
    }

    if (match) {
      key = match[1];
      value = match[2].trim();

      if (key === itemStarterKey && Object.keys(currentItem).length > 0) {
        items.push({ ...currentItem });
        console.log("[parseRowsToData] 📦 Item completed", {
          currentItem,
          items,
        });
        currentItem = {};
      }

      currentItem[key] = value;
      console.log("[parseRowsToData] 📝 Key-value processed", {
        line,
        key,
        value,
        currentItem,
      });
    } else {
      if (Object.keys(currentItem).length > 0) {
        const keys = Object.keys(currentItem);
        if (keys.length > 0) {
          const lastKey = keys[keys.length - 1];
          currentItem[lastKey] = currentItem[lastKey] + " " + line;
          console.log("[parseRowsToData] 📝 Continuation line added", {
            line,
            lastKey,
            currentItem,
          });
        } else {
          currentItem["value"] = line;
        }
      } else {
        currentItem["value"] = line;
      }
    }
  }

  if (Object.keys(currentItem).length > 0) {
    items.push({ ...currentItem });
    console.log("[parseRowsToData] 📦 Final item added", {
      currentItem,
      items,
    });
  }

  console.log("[parseRowsToData] ✅ Completed", { items });
  return items.length > 0 ? items : null;
};

// ============================================================
// IMAGE URL DETECTION & RENDERING
// ============================================================

/**
 * Check if a string is an image URL
 */
export const isImageUrl = (text) => {
  if (!text || typeof text !== "string") return false;
  const imageExtensions =
    /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?.*)?$/i;
  if (imageExtensions.test(text)) return true;
  if (
    text.match(
      /^https?:\/\/[^\s]+(images|photos|pics|img|assets|uploads|media)\/[^\s]+/i,
    )
  )
    return true;
  const imageHosts = [
    "images.pexels.com",
    "unsplash.com",
    "imgur.com",
    "i.imgur.com",
    "cloudinary.com",
    "res.cloudinary.com",
    "s3.amazonaws.com",
    "storage.googleapis.com",
    "cdn.",
    "photobucket.com",
    "flickr.com",
    "live.com",
  ];
  for (const host of imageHosts) {
    if (text.toLowerCase().includes(host)) return true;
  }
  return false;
};

/**
 * Extract image URLs from a block of text
 * Returns array of { url, context } objects
 */
export const extractImageUrls = (text) => {
  console.log("HERE IS text in extractImageUrls", text);

  if (!text || typeof text !== "string") return [];

  const results = [];

  // Tag agnostic: look for data-image-url attribute on ANY element
  const dataAttrRegex = /data-image-url="([^"]+)"/g;
  let match;
  while ((match = dataAttrRegex.exec(text)) !== null) {
    const url = match[1];
    if (isImageUrl(url)) {
      // Try to find the containing element - tag agnostic
      const elementMatch = text.match(
        new RegExp(
          `<[^>]*class="[^"]*value[^"]*image-url[^"]*"[^>]*data-image-url="${escapeRegExp(url)}"[^>]*>([^<]*)<\\/[^>]+>`,
        ),
      );
      if (elementMatch) {
        const actualText = elementMatch[1] || url;
        results.push({ url, original: actualText, context: text });
      } else {
        results.push({ url, original: url, context: text });
      }
    }
  }

  if (results.length > 0) {
    console.log("[extractImageUrls] Found via data-image-url:", results);
    return results;
  }

  const parts = text.split(/\s+/);
  for (const part of parts) {
    const cleaned = part.replace(/[.,;:!?()'"\[\]{}<>]+$/, "");
    if (isImageUrl(cleaned)) {
      const exists = results.some((r) => r.url === cleaned);
      if (!exists) {
        results.push({ url: cleaned, original: part, context: text });
      }
    }
  }

  console.log("[extractImageUrls] Found via fallback:", results);
  return results;
};

// Helper to escape regex special characters
export const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};


// PreviewTester/utils/renderingUtils.js
/**
 * Strip ONLY the structural empty-line rows that surround iteration wrappers.
 * User-intentional interior empty-lines are preserved.
 *
 * Structural empty-lines we strip:
 *   • direct children of for-loop-started before the first [data-iter-id]
 *   • direct children of for-loop-started after  the last  [data-iter-id]
 *   • first child of each [data-iter-id] if it is an empty-line
 *   • last  child of each [data-iter-id] if it is an empty-line
 */
export const stripStructuralEmptyLines = (html) => {
  if (!html || typeof html !== 'string') return html;
  if (!html.includes('data-iter-id')) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return html;

  const isEmptyLine = (el) =>
    el &&
    el.classList &&
    el.classList.contains('row') &&
    el.classList.contains('empty-line');

  const isIterWrapper = (el) =>
    el && el.hasAttribute && el.hasAttribute('data-iter-id');

  // ------------------------------------------------------------
  // 1. Inside each for-loop-started: strip EVERY direct-child
  //    empty-line that is NOT inside an iter wrapper. That is:
  //      • before the first iter wrapper
  //      • between two iter wrappers
  //      • after the last iter wrapper
  //    Any iter wrapper's interior rows are untouched.
  // ------------------------------------------------------------
  root.querySelectorAll('.for-loop-started').forEach((loop) => {
    Array.from(loop.children).forEach((child) => {
      if (isIterWrapper(child)) return;   // never touch wrappers
      if (isEmptyLine(child)) child.remove();
    });
  });

  // ------------------------------------------------------------
  // 2. Inside each iter wrapper: strip only the FIRST and LAST
  //    child if they are empty-lines. Interior empties survive.
  // ------------------------------------------------------------
  root.querySelectorAll('[data-iter-id]').forEach((wrapper) => {
    const first = wrapper.firstElementChild;
    if (isEmptyLine(first)) first.remove();

    const last = wrapper.lastElementChild;
    if (isEmptyLine(last)) last.remove();
  });

  return root.innerHTML;
};


export const wrapFieldValuesWithSpans = (
  text,
  templateLines = [],
  isSampleArray = false,
  isSingleItem = false,
) => {
  console.log(text, "[wrapFieldValuesWithSpans] START", JSON.stringify(templateLines));
  console.log("[wrapFieldValuesWithSpans] text length:", text?.length || 0);
  console.log("[wrapFieldValuesWithSpans] templateLines length:", templateLines?.length || 0);

  if (!text) return text;

  // ============================================================
  // Helper: Check if content contains HTML
  // ============================================================
  const hasHtml = (content) => {
    return /<[a-z][\s\S]*>/i.test(content);
  };

  // ============================================================
  // Helper: Check if a string is an image URL
  // ============================================================
  const isImageUrl = (text) => {
    if (!text || typeof text !== "string") return false;
    const imageExtensions =
      /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?.*)?$/i;
    if (imageExtensions.test(text)) return true;
    if (
      text.match(
        /^https?:\/\/[^\s]+(images|photos|pics|img|assets|uploads|media)\/[^\s]+/i,
      )
    )
      return true;
    const imageHosts = [
      "images.pexels.com",
      "unsplash.com",
      "imgur.com",
      "i.imgur.com",
      "cloudinary.com",
      "res.cloudinary.com",
      "s3.amazonaws.com",
      "storage.googleapis.com",
      "cdn.",
      "photobucket.com",
      "flickr.com",
      "live.com",
    ];
    for (const host of imageHosts) {
      if (text.toLowerCase().includes(host)) return true;
    }
    return false;
  };

  // ============================================================
  // Helper: Extract type information from data attributes
  // ============================================================
  const extractTypeInfo = (html) => {
    const typeMatch = html.match(/data-template-type="([^"]+)"/);
    const idMatch = html.match(/data-template-id="([^"]+)"/);
    return {
      type: typeMatch ? typeMatch[1] : null,
      id: idMatch ? idMatch[1] : null,
    };
  };

  // ============================================================
  // Helper: Extract text content from HTML
  // ============================================================
  const extractTextContent = (html) => {
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // ============================================================
  // Helper: Get button text from button
  // ============================================================
  const getButtonText = (button) => {
    if (!button) return '';
    if (typeof button === 'string') return button;
    if (typeof button === 'object' && button !== null) {
      return button.text || button.label || button.name || button.value || String(button);
    }
    return String(button);
  };

  // ============================================================
  // Helper: Check if a line is a section header
  // ============================================================
  const isSectionHeader = (trimmed) => {
    return /^[A-Za-z\s]+:$/.test(trimmed);
  };

  // ============================================================
  // Helper: Check if a template line has a variable
  // ============================================================
  const hasTemplateVariable = (content) => {
    if (!content) return false;
    return /{{[^}]+}}/.test(content);
  };

  // ============================================================
  // Helper: Get the template line for a given text content
  // ============================================================
  const findMatchingTemplateLine = (trimmedContent, templateLines) => {
    if (!templateLines || templateLines.length === 0) return null;

    // Try to find by exact content match
    for (const line of templateLines) {
      if (line.content && line.content.trim() === trimmedContent) {
        return line;
      }
    }

    // Try to find by checking if the template line contains the trimmed content
    // This handles cases where the rendered content is a subset of the template
    for (const line of templateLines) {
      if (line.content) {
        const templateTrimmed = line.content.trim();
        // Check if the template line has a variable and the rendered content is part of it
        if (hasTemplateVariable(templateTrimmed)) {
          // Extract the key from the template line
          const kvMatch = templateTrimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*([-:=>]+)\s*.*$/);
          if (kvMatch) {
            const key = kvMatch[1];
            // If the rendered content starts with the key and a separator, it's a match
            const separatorPattern = new RegExp(`^${key}\\s*[-:=>]+`);
            if (separatorPattern.test(trimmedContent)) {
              return line;
            }
          }
        }
      }
    }

    return null;
  };

  // ============================================================
  // Helper: Check if a line is a key-value pair with template variable
  // ============================================================
  const getKeyValueInfo = (trimmed, templateLine = null) => {
    const kvMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*([-:=>]+)\s*(.+)$/);
    if (kvMatch) {
      const key = kvMatch[1];
      const separator = kvMatch[2];
      const value = kvMatch[3];

      // Check if the template line has a variable
      let hasVariable = false;

      if (templateLine && templateLine.content) {
        // Check if the original template content has a variable
        const originalContent = templateLine.content;
        // Check if the value part of the template has {{ ... }}
        const varPattern = new RegExp(`${key}\\s*${separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\{\\{[^}]+\\}\\}`);
        if (varPattern.test(originalContent)) {
          hasVariable = true;
        }
      }

      // If no variable, treat as plain text
      if (!hasVariable) {
        return null;
      }

      return { key, separator, value, hasVariable: true };
    }
    return null;
  };

  const isPureImageUrl = (text) => {
    if (!text || typeof text !== "string") return false;
    const trimmed = text.trim();
    // Must start with http:// or https://
    if (!/^https?:\/\//i.test(trimmed)) return false;
    // Must have an image extension at the end
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?.*)?$/i;
    return imageExtensions.test(trimmed);
  };
  // ============================================================
  // Helper: Process content into key/sep/value architecture
  // ============================================================
  const processContent = (content, indent = "", templateLine = null) => {
    const trimmed = content.trim();

    if (!trimmed) {
      return `${indent}<div class="row empty-line"></div>`;
    }

    // Check if this is a Section Header
    if (isSectionHeader(trimmed)) {
      return `${indent}<div class="row"><div class="section-header">${trimmed}</div></div>`;
    }

    // ✅ NEW: Check if this is a pure image URL (just the URL, no key)
    if (isPureImageUrl(trimmed)) {
      const url = trimmed;
      return `${indent}<div class="row"><div class="value image-url" data-image-url="${url}">${url}</div></div>`;
    }

    // Check if this is a Key-Value Pair with template variable
    const kvInfo = getKeyValueInfo(trimmed, templateLine);
    if (kvInfo) {
      const { key, separator, value } = kvInfo;
      const isImage = isImageUrl(value.trim());

      let valueHtml;
      if (isImage) {
        valueHtml = `<div class="value image-url" data-image-url="${value.trim()}">${value}</div>`;
      } else {
        valueHtml = `<div class="value">${value}</div>`;
      }

      const result =
        `<div class="key">${key}</div>` +
        `<div class="separator"> ${separator} </div>` +
        valueHtml;

      return `${indent}<div class="row">${result}</div>`;
    }

    // Mix Row (text + image + text)
    const mixMatch = trimmed.match(/^(.*?)(https?:\/\/[^\s]+)(.*)$/);
    if (mixMatch) {
      const before = mixMatch[1].trim();
      const url = mixMatch[2].trim();
      const after = mixMatch[3].trim();

      if (isImageUrl(url) && (before || after)) {
        let cells = [];
        if (before) {
          cells.push(`<div class="text-cell">${before}</div>`);
        }
        cells.push(`<div class="image-cell" data-image-url="${url}">${url}</div>`);
        if (after) {
          cells.push(`<div class="text-cell">${after}</div>`);
        }

        return `${indent}<div class="row mix" style="display:flex;flex-wrap:wrap;gap:4px;align-items:center;padding:2px 0;">${cells.join('')}</div>`;
      }
    }

    // Plain text
    return `${indent}<div class="row">${trimmed}</div>`;
  };

  // ============================================================
  // Build a map of template lines by ID for quick lookup
  // ============================================================
  const templateLineById = new Map();
  if (templateLines && Array.isArray(templateLines)) {
    templateLines.forEach((line) => {
      if (line.id) {
        templateLineById.set(line.id, line);
      }
    });
  }

  // ============================================================
  // Helper: Process a typed line - preserve type but process content
  // ============================================================
  const processTypedLine = (line, indent = "") => {
    const typeInfo = extractTypeInfo(line);

    if (!typeInfo.type || !typeInfo.id) {
      return null;
    }

    console.log(`[wrapFieldValuesWithSpans] 📋 Processing typed line: type=${typeInfo.type}, id=${typeInfo.id}`);

    const textContent = extractTextContent(line);
    const templateLine = templateLineById.get(typeInfo.id);

    // ============================================================
    // BUTTON
    // ============================================================
    if (typeInfo.type === 'button') {
      const buttonText = textContent || 'Button';
      return `${indent}<div class="row button" data-template-type="${typeInfo.type}" data-template-id="${typeInfo.id}">${buttonText}</div>`;
    }

    // ============================================================
    // BUTTON GROUP
    // ============================================================
    if (typeInfo.type === 'button-group') {
      let buttons = [];

      if (templateLine && templateLine.buttons && Array.isArray(templateLine.buttons)) {
        buttons = templateLine.buttons;
      } else {
        const buttonMatch = textContent.match(/\[Button Group\]\s*(.+)/);
        if (buttonMatch) {
          buttons = buttonMatch[1].split(',').map(b => b.trim()).filter(b => b);
        } else {
          buttons = ['Button 1', 'Button 2'];
        }
      }

      if (buttons.length > 0) {
        console.log(`[wrapFieldValuesWithSpans] 📦 Rendering button group with ${buttons.length} buttons`);
        const buttonsHtml = buttons.map((btn) => {
          const text = getButtonText(btn);
          return `<button class="button-cell" style="padding:6px 16px;background:#4299e1;color:white;border:none;border-radius:4px;font-size:12px;cursor:default;margin:2px;">${text}</button>`;
        }).join('');
        return `${indent}<div class="row button-group" data-template-type="${typeInfo.type}" data-template-id="${typeInfo.id}" style="display:flex;flex-wrap:wrap;gap:6px;padding:4px 0;align-items:center;">${buttonsHtml}</div>`;
      }
    }

    // ============================================================
    // For all other types, find the template line and process
    // ============================================================
    let templateLineForContent = templateLine;

    // If no template line by ID, try to find by content
    if (!templateLineForContent) {
      templateLineForContent = findMatchingTemplateLine(textContent, templateLines);
    }

    const processedContent = processContent(textContent, indent, templateLineForContent);

    if (processedContent.includes('<div class="row"')) {
      const withAttributes = processedContent
        .replace(
          '<div class="row"',
          `<div class="row" data-template-type="${typeInfo.type}" data-template-id="${typeInfo.id}"`
        );
      return withAttributes;
    }

    return processedContent;
  };

  const lines = text.split("\n");
  const wrappedLines = [];

  for (const line of lines) {
    const trimmed = line.trim ? line.trim() : String(line).trim();
    const indent = line.match(/^(\s*)/)?.[1] || "";

    if (!trimmed) {
      wrappedLines.push(`${indent}<div class="row empty-line"></div>`);
      continue;
    }

    // ============================================================
    // STEP 1: Check if the line has type information
    // ============================================================
    const typeInfo = extractTypeInfo(line);

    if (typeInfo.type && typeInfo.id) {
      const processed = processTypedLine(line, indent);
      if (processed) {
        wrappedLines.push(processed);
        continue;
      }
    }

    // ============================================================
    // STEP 2: If the line already contains HTML but no type info
    // ============================================================
    if (line.includes("<div") || line.includes("</div>")) {
      if (line.includes('class="for-loop-started"')) {
        wrappedLines.push(line);
        continue;
      }

      if (line.includes('class="row empty-line"')) {
        wrappedLines.push(line);
        continue;
      }

      const textContent = extractTextContent(line);
      if (textContent) {
        // Try to find a matching template line
        const templateLine = findMatchingTemplateLine(textContent, templateLines);
        const processed = processContent(textContent, indent, templateLine);
        wrappedLines.push(processed);
        continue;
      }

      wrappedLines.push(line);
      continue;
    }

    // ============================================================
    // STEP 3: Plain text line - try to find matching template line
    // ============================================================
    const templateLine = findMatchingTemplateLine(trimmed, templateLines);
    const processed = processContent(trimmed, indent, templateLine);
    wrappedLines.push(processed);
  }

  const result = wrappedLines.join("\n");
  console.log(
    "[wrapFieldValuesWithSpans] ✅ Wrapping complete, length:",
    result.length,
  );
  return result;
};

// ============================================================
// FIND ELEMENT BY PATH
// ============================================================

/**
 * Resolve a path to a DOM element.
 *
 * TWO DOMs are in play:
 *
 *   LIVE DOM (paths are generated against this):
 *     .item-ui-screen
 *       └─ <div>              ← React wrapper (dangerouslySetInnerHTML)
 *           └─ .contentWraps
 *               └─ .row / ...
 *
 *   STRING DOM (renderedPreview, where paths are resolved):
 *     .item-ui-screen
 *       └─ .contentWraps
 *           └─ .row / ...
 *
 * RULES OBSERVED:
 *   R1. Outside-loop selectors are relative to .item-ui-screen.
 *   R2. Inside-loop selectors are relative to loopSelector; loopSelector
 *       itself is relative to .item-ui-screen.
 *   R3. The React wrapper is always present in the live DOM.
 *
 * RESPONSIBILITY:
 *   When the container is a string-DOM .item-ui-screen (no wrapper),
 *   strip exactly one leading segment from the path so the same
 *   stored path resolves correctly against both DOMs.
 *
 *   The strip ONLY happens when the container is genuinely a
 *   .item-ui-screen. For any other container (.for-loop-started,
 *   .row, etc.) the path is used as-is.
 */
export const findElementByPath = (container, path) => {
  console.log("[findElementByPath] ========================================");
  console.log(
    "[findElementByPath] 📦 Container:",
    container?.className || container?.tagName,
  );
  console.log("[findElementByPath] 🗺️ Path:", path);
  console.log("[findElementByPath] ========================================");

  if (!container || !path) {
    console.log("[findElementByPath] ❌ Missing container or path");
    return null;
  }

  if (!path.trim()) {
    console.log("[findElementByPath] ⚠️ Empty path, returning container");
    return container;
  }

  // ============================================================
  // ✅ EXPLICIT WRAPPER DETECTION
  //
  // The strip only applies when:
  //   • the container is a .item-ui-screen, AND
  //   • its first child is a plain <div> that contains .contentWraps
  //     (i.e. the React wrapper).
  //
  // Any other container (loop, row, etc.) never strips.
  // ============================================================
  const isScreenContainer =
    container.classList && container.classList.contains("item-ui-screen");

  const firstChild = container.firstElementChild;
  const hasWrapper = !!(
    isScreenContainer &&
    firstChild &&
    firstChild.tagName === "DIV" &&
    !(firstChild.classList && firstChild.classList.contains("contentWraps")) &&
    firstChild.querySelector &&
    firstChild.querySelector(":scope > .contentWraps")
  );

  console.log(
    "[findElementByPath] 🔎 isScreenContainer:",
    isScreenContainer,
    "hasWrapper:",
    hasWrapper,
  );

  // ============================================================
  // ✅ DEV-ONLY GUARD — 1A (segment shape)
  // INVARIANT: Every segment must match `tag:nth-child(N)`.
  //
  // Reason: the resolver walks by tag + nth-child. Any segment
  // that does not match this regex will cause resolvePath to bail
  // with "Invalid path part" — silently returning null. A wrong
  // path shape from an upstream generator therefore fails silently.
  //
  // Owned by: getNthChildPath, getRelativePath, and
  //           generateSelectorPathForRow.
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    const rawParts = path.split(" > ");
    const segRe = /^[a-zA-Z]+:nth-child\(\d+\)$/;
    const badSeg = rawParts.find((p) => !segRe.test(p.trim()));
    if (badSeg) {
      console.warn(
        "[INVARIANT:1A] findElementByPath received a malformed segment.",
        {
          path,
          badSegment: badSeg,
          container: container?.className || container?.tagName,
        },
      );
    }
    if (rawParts.length < 2) {
      console.warn(
        "[INVARIANT:1A] findElementByPath received a very short path — verify generator emits the wrapper > contentWraps prefix.",
        {
          path,
          partCount: rawParts.length,
          container: container?.className || container?.tagName,
        },
      );
    }
  }

  // ============================================================
  // ✅ DEV-ONLY GUARD — 1A (path/container space mismatch)
  // INVARIANT: A multi-segment path is only valid against a screen
  //            container. Any other container (loop, row, leaf)
  //            is a different path-space and will silently fail to
  //            resolve.
  //
  // Reason: A caller that passes a multi-segment path with a
  // non-screen container has almost certainly confused the two
  // path-spaces (absolute vs. relative-to-loop). The resolver
  // would walk from the wrong origin.
  //
  // Owned by: every caller of findElementByPath — audit if this
  //           warning fires.
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    if (path.includes(" > ") && !isScreenContainer) {
      console.warn(
        "[INVARIANT:1A] Multi-segment path resolved against non-screen container.",
        {
          containerClass: container.className,
          containerTag: container.tagName,
          path,
        },
      );
    }
  }

  let effectivePath = path;

  if (isScreenContainer && !hasWrapper) {
    const rawParts = path.split(" > ");
    if (rawParts.length > 1) {
      effectivePath = rawParts.slice(1).join(" > ");
      console.log(
        "[findElementByPath] 🔄 No React wrapper detected — dropping leading segment:",
        effectivePath,
      );
    } else {
      console.log(
        "[findElementByPath] ⚠️ No wrapper and only one segment — using path as-is:",
        path,
      );
    }
  }

  // ============================================================
  // Resolution helper — walks a path from `container`.
  // ============================================================
  const resolvePath = (p) => {
    const parts = p.split(" > ");
    let current = container;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const match = part.match(/^([a-zA-Z]+):nth-child\((\d+)\)$/);

      if (!match) {
        console.log(`[findElementByPath] ❌ Invalid path part: ${part}`);
        return null;
      }

      const tagName = match[1];
      const nthChild = parseInt(match[2], 10);

      console.log(
        `[findElementByPath] 🔍 Step ${i + 1}/${parts.length}: Looking for ${tagName}:nth-child(${nthChild})`,
      );

      const children = Array.from(current.children).filter(
        (el) => el.tagName.toLowerCase() === tagName,
      );

      if (nthChild > 0 && nthChild <= children.length) {
        current = children[nthChild - 1];
        console.log(
          `[findElementByPath] ✅ Found ${tagName}:nth-child(${nthChild})`,
        );
      } else {
        console.log(
          `[findElementByPath] ❌ Could not find ${tagName}:nth-child(${nthChild})`,
        );
        console.log(
          `[findElementByPath] Available children with tag ${tagName}:`,
          children.map((c, idx) => `${idx + 1}: ${c.tagName}.${c.className}`),
        );
        return null;
      }
    }

    return current;
  };

  // ============================================================
  // Try the effective path. If it fails and we dropped a segment,
  // fall back to the original path (handles any legacy stored
  // paths that were emitted without the wrapper segment).
  // ============================================================
  let result = resolvePath(effectivePath);

  if (!result && effectivePath !== path) {
    console.log(
      "[findElementByPath] ↩ Effective path failed — retrying with original path:",
      path,
    );
    result = resolvePath(path);
  }

  if (!result) {
    console.log("[findElementByPath] ❌ FAILED");
    return null;
  }

  console.log("[findElementByPath] ✅ FINAL element found:", {
    tag: result?.tagName,
    class: result?.className,
    text: result?.textContent?.trim()?.substring(0, 100),
  });
  console.log("[findElementByPath] ✅ SUCCESS");
  return result;
};

// ============================================================
// GET NTH-CHILD PATH
// ============================================================

/**
 * Get nth-child path for an element, always relative to `.item-ui-screen`.
 *
 * RULES OBSERVED:
 *   R1. Outside-loop selectors are relative to .item-ui-screen.
 *   R3. The React wrapper <div> between .item-ui-screen and .contentWraps
 *       is ALWAYS part of the path (as div:nth-child(1) at the head).
 *
 * The path is guaranteed to be resolvable against the STRING DOM by
 * findElementByPath, because findElementByPath detects the missing
 * wrapper and strips exactly one leading segment.
 *
 * Result shapes (5 segments = leaf, 4 = row) when called with
 * .item-ui-screen as container:
 *   row-level:   wrapper → contentWraps → row
 *   leaf-level:  wrapper → contentWraps → row → leaf
 *
 * CONTAINER-AWARE:
 *   If the parent in the walk is a .for-loop-started container, only
 *   children that carry [data-iter-id] are counted for nth-child
 *   positions. This keeps iteration positions independent of any
 *   decorative siblings (phase indicators, etc.) that may be appended
 *   to the loop container at runtime.
 */
export const getNthChildPath = (element, container) => {
  console.log("[getNthChildPath] ========== START ==========");
  console.log("[getNthChildPath] element:", {
    tag: element?.tagName,
    class: element?.className,
    text: element?.textContent?.trim()?.substring(0, 30),
  });
  console.log("[getNthChildPath] container:", {
    tag: container?.tagName,
    class: container?.className,
  });

  if (!element || !container) return "";
  if (element === container) return "";

  // STRICT VALIDATION: container MUST be item-ui-screen
  if (!container.classList || !container.classList.contains("item-ui-screen")) {
    console.error("[getNthChildPath] ❌ container is not item-ui-screen", {
      tagName: container.tagName,
      className: container.className,
    });
    throw new Error(
      `container must be item-ui-screen, got: ${container.className || container.tagName}`,
    );
  }

  // ============================================================
  // ✅ Walk up, emitting every segment EXCEPT the container itself.
  //
  // The React wrapper (dangerouslySetInnerHTML) between the screen
  // and .contentWraps is intentionally INCLUDED in the path, so
  // recalculation logic that reasons about depth sees the live
  // tree shape.
  //
  // For parents that are .for-loop-started, count ONLY [data-iter-id]
  // children so iteration positions are immune to decorative siblings
  // (phase rings, badges, etc.).
  // ============================================================
  const path = [];
  let current = element;
  let depth = 0;

  while (current && current !== container) {
    const parent = current.parentElement;
    if (!parent) break;

    const isLoopParent =
      parent.classList && parent.classList.contains("for-loop-started");

    // ============================================================
    // ✅ DEV-ONLY GUARD — 1B
    // INVARIANT: When walking through a .for-loop-started parent,
    //            ONLY children with [data-iter-id] are counted for
    //            nth-child positions. Decorative siblings (phase
    //            ring, phase badge) MUST be excluded.
    //
    // Reason: applyPhaseIndicators appends ring/badge <div>s inside
    // every loop container at runtime. If those siblings were
    // counted, iteration positions would drift on every render
    // where the indicators were present.
    //
    // Owned by: applyPhaseIndicators in PreviewContainer.js
    // ============================================================
    if (process.env.NODE_ENV !== "production") {
      if (isLoopParent) {
        const allRawChildren = Array.from(parent.children);
        const iterChildren = allRawChildren.filter(
          (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
        );
        const filteredChildren = allRawChildren.filter(
          (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
        );
        // Sanity: the filter is being applied and only iter wrappers survive.
        if (filteredChildren.length !== iterChildren.length) {
          console.warn(
            "[INVARIANT:1B] getNthChildPath iter-only filter leaked non-iter children.",
            {
              parentClass: parent.className,
              totalChildren: allRawChildren.length,
              iterChildren: iterChildren.length,
              filteredChildren: filteredChildren.length,
            },
          );
        }
        // Sanity: there must be at least one iter wrapper for a walk
        // to have a valid starting position.
        if (iterChildren.length === 0) {
          console.warn(
            "[INVARIANT:1B] getNthChildPath walked a .for-loop-started with zero [data-iter-id] children.",
            {
              parentClass: parent.className,
              totalChildren: allRawChildren.length,
            },
          );
        }
      }
    }

    const allChildren = isLoopParent
      ? Array.from(parent.children).filter(
          (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
        )
      : Array.from(parent.children);

    const index = allChildren.indexOf(current);
    if (index >= 0) {
      const tagName = current.tagName.toLowerCase();
      path.unshift(`${tagName}:nth-child(${index + 1})`);
      console.log(
        `[getNthChildPath] ➕ Added: ${tagName}:nth-child(${index + 1}) (position ${index + 1} of ${allChildren.length})`,
      );
    } else {
      console.log(
        `[getNthChildPath] ⚠️ Skipped (not in filtered children):`,
        current.tagName,
        current.className,
      );
    }

    current = parent;
    depth++;
    if (depth > 20) {
      console.log("[getNthChildPath] ⚠️ Max depth reached");
      break;
    }
  }

  const result = path.join(" > ");

  // ============================================================
  // ✅ DEV-ONLY GUARD — 1B (second insertion — output shape)
  // INVARIANT: Absolute paths MUST start with:
  //              div:nth-child(1) > div:nth-child(1)
  //            i.e. wrapper > contentWraps.
  //
  // Reason: findElementByPath strips exactly ONE leading segment
  // when resolving against the string DOM. If the wrapper is
  // missing from the emitted path, the strip will remove the
  // wrong segment and resolution will fail silently.
  //
  // Owned by: the React tree shape in PreviewContainer +
  //           renderScreens' string DOM output.
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    if (result) {
      const parts = result.split(" > ");
      const expectedHead = "div:nth-child(1) > div:nth-child(1)";
      if (!result.startsWith(expectedHead)) {
        console.warn(
          "[INVARIANT:1B] getNthChildPath produced a path without the wrapper > contentWraps prefix.",
          {
            result,
            expectedHead,
            parts,
            elementTag: element?.tagName,
            elementClass: element?.className,
          },
        );
      }
    }
  }

  console.log(
    `[getNthChildPath] ✅ Path (relative to item-ui-screen, wrapper INCLUDED): ${result}`,
  );
  console.log("[getNthChildPath] ========== END ==========");
  return result;
};


// ============================================================
// GET RELATIVE PATH
// ============================================================

/**
 * Get relative path for an element, relative to its loop container
 * (.for-loop-started) or any other ancestor container.
 *
 * RULES OBSERVED:
 *   R2. Inside-loop selectors are relative to the loop container.
 *   R4. Shallow (row-level) when the selection covers a full row;
 *       deep (leaf-level) when partial.
 *
 * CONTAINER-AWARE:
 *   When the parent in the walk is a .for-loop-started container,
 *   ONLY children with [data-iter-id] are counted for nth-child
 *   positions. This makes the iteration position independent of
 *   decorative siblings (phase rings, badges) that may live inside
 *   the loop container at runtime.
 *
 * SHALLOW vs DEEP — BOTH INCLUDE THE ITERATION WRAPPER:
 *
 *   In a wrapped loop the DOM is:
 *
 *     .for-loop-started
 *       └─ div[data-iter-id="0"]     ← iteration wrapper
 *           └─ .row                  ← row
 *               └─ .key / .value     ← leaf
 *
 *   Relative to .for-loop-started:
 *     • Full-row (shallow): iter-wrapper > row
 *         e.g. "div:nth-child(1) > div:nth-child(2)"
 *     • Partial (deep):     iter-wrapper > row > leaf
 *         e.g. "div:nth-child(1) > div:nth-child(2) > div:nth-child(1)"
 *
 *   The iteration wrapper segment is ALWAYS present — it is a real
 *   DOM node between the loop container and the row and MUST be
 *   counted, whether the target is a row or a leaf inside a row.
 *
 *   (Previous versions of this function used to `break` early once
 *   the target row was reached, which erroneously omitted the
 *   iteration wrapper from shallow paths. That bug is fixed here.)
 */
export const getRelativePath = (targetElement, container) => {
  console.log("[getRelativePath] START", targetElement, container);
  console.log("[getRelativePath] targetElement:", targetElement);
  console.log("[getRelativePath] container:", container);

  if (!targetElement) {
    console.log("[getRelativePath] ⚠️ Missing targetElement");
    return "";
  }

  if (!container) {
    console.error("[getRelativePath] ❌ container is null or undefined");
    throw new Error("container is required");
  }

  if (!container.classList) {
    console.warn(
      "[getRelativePath] ⚠️ container has no classList, but continuing",
    );
  }

  if (targetElement === container) {
    console.log("[getRelativePath] ⚠️ targetElement is container");
    return "";
  }

  // Safety: Ensure container is actually an ancestor of targetElement
  let isAncestor = false;
  let check = targetElement.parentElement;
  while (check) {
    if (check === container) {
      isAncestor = true;
      break;
    }
    check = check.parentElement;
  }

  if (!isAncestor) {
    console.warn(
      "[getRelativePath] ⚠️ container is not an ancestor of targetElement",
    );
    const fallbackContainer = targetElement.closest(".for-loop-started");
    if (fallbackContainer) {
      console.log(
        "[getRelativePath] 🔄 Using fallback container:",
        fallbackContainer,
      );
      container = fallbackContainer;
    } else {
      console.log("[getRelativePath] ⚠️ Could not find fallback container");
      return "";
    }
  }

  // ============================================================
  // ✅ DEV-ONLY GUARD — 1C (container type)
  // INVARIANT: getRelativePath must be called with a container that
  //            is one of:
  //              • .for-loop-started  (inside-loop relative path)
  //              • .item-ui-screen    (degenerate / top-level)
  //              • .contentWraps      (defensive)
  //
  // Reason: any other container (a .row, a leaf node, an arbitrary
  // ancestor) means the caller is confused about the path space.
  // The resulting relative path will not be relative to the loop
  // container — it will be relative to something else.
  //
  // Owned by: generatePositionSelector in transformationRules.js
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    if (
      container &&
      container.classList &&
      !container.classList.contains("for-loop-started") &&
      !container.classList.contains("item-ui-screen") &&
      !container.classList.contains("contentWraps")
    ) {
      console.warn(
        "[INVARIANT:1C] getRelativePath called with an unexpected container type.",
        {
          tag: container.tagName,
          className: container.className,
          targetTag: targetElement?.tagName,
          targetClass: targetElement?.className,
        },
      );
    }
  }

  const path = [];
  let current = targetElement;
  let depth = 0;

  // Traverse up from targetElement until we hit the container.
  //
  // NOTE: we do NOT break early when the target is a row. The walk
  // must continue all the way up to the container so that the
  // iteration wrapper (a [data-iter-id] div sitting between the row
  // and the loop container) is included in the path.
  while (current && current !== container) {
    console.log(`[getRelativePath] 🔍 Current:`, {
      tagName: current.tagName,
      className: current.className,
      textContent: current.textContent?.trim()?.substring(0, 30),
    });

    const parent = current.parentElement;
    if (!parent) {
      console.log(`[getRelativePath] ⚠️ No parent element`);
      break;
    }

    // ============================================================
    // ✅ CONTAINER-AWARE CHILD COUNTING
    //
    // When the parent is a .for-loop-started, count ONLY the
    // [data-iter-id] children for nth-child positions. This keeps
    // iteration positions stable regardless of decorative siblings
    // (phase ring, phase badge) that the wizard may append.
    //
    // Fallback: if the loop container has no [data-iter-id] children
    // (legacy unwrapped loop), fall back to counting all children
    // so that legacy paths still resolve.
    // ============================================================
    const isLoopParent =
      parent.classList && parent.classList.contains("for-loop-started");

    // ============================================================
    // ✅ DEV-ONLY GUARD — 1C (iter-only counting)
    // INVARIANT: When walking through a .for-loop-started parent,
    //            ONLY [data-iter-id] children are counted.
    //
    // Reason: decorative siblings (phase ring, phase badge) must
    // not shift iteration positions. Otherwise the relative path
    // for iter[0] would change depending on whether the phase
    // indicators were currently rendered.
    //
    // Owned by: applyPhaseIndicators in PreviewContainer.js
    // ============================================================
    if (process.env.NODE_ENV !== "production") {
      if (isLoopParent) {
        const allRawChildren = Array.from(parent.children);
        const iterChildren = allRawChildren.filter(
          (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
        );
        if (iterChildren.length === 0) {
          console.warn(
            "[INVARIANT:1C] getRelativePath walked a .for-loop-started with zero [data-iter-id] children.",
            {
              parentClass: parent.className,
              totalChildren: allRawChildren.length,
            },
          );
        }
        if (allRawChildren.length !== iterChildren.length) {
          const filteredChildren = allRawChildren.filter(
            (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
          );
          if (filteredChildren.length !== iterChildren.length) {
            console.warn(
              "[INVARIANT:1C] getRelativePath iter-only filter mismatch.",
              {
                parentClass: parent.className,
                totalChildren: allRawChildren.length,
                iterChildren: iterChildren.length,
                filteredChildren: filteredChildren.length,
              },
            );
          }
        }
      }
    }

    const iterChildren =
      isLoopParent
        ? Array.from(parent.children).filter(
            (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
          )
        : null;

    const allChildren =
      isLoopParent && iterChildren && iterChildren.length > 0
        ? iterChildren
        : Array.from(parent.children);

    const index = allChildren.indexOf(current);

    if (index >= 0) {
      const tagName = current.tagName.toLowerCase();
      const nthChild = index + 1;
      const part = `${tagName}:nth-child(${nthChild})`;
      path.unshift(part);
      console.log(
        `[getRelativePath] ➕ Added: ${part} (position ${nthChild} of ${allChildren.length} children${isLoopParent && iterChildren && iterChildren.length > 0 ? " [iter-only]" : ""})`,
      );
    } else {
      console.log(`[getRelativePath] ⚠️ Could not find index for element`);
    }

    current = parent;
    depth++;

    if (depth > 20) {
      console.log("[getRelativePath] ⚠️ Max depth reached");
      break;
    }
  }

  const result = path.join(" > ");
  console.log("[getRelativePath] ✅ Relative path:", result);
  return result;
};

/**
 * Determine whether a loop container uses the new per-iteration wrapper scheme.
 */
/**
 * Determine whether a loop container uses the new per-iteration wrapper scheme.
 */
const isWrappedLoop = (loopContainer) =>
  !!loopContainer && loopContainer.getAttribute('data-wrap-iterations') === 'true';

/**
 * Return the per-iteration row groups for a wrapped loop container.
 * Each group: { iterId, wrapper, rows: Element[] }.
 * Returns null if the loop is not wrapped (legacy path).
 */
const getIterationGroups = (loopContainer) => {
  if (!loopContainer) return null;
  if (!isWrappedLoop(loopContainer)) return null;

  const iterWrappers = Array.from(loopContainer.children).filter(
    (el) => el.hasAttribute && el.hasAttribute('data-iter-id')
  );
  if (iterWrappers.length === 0) return null;

  return iterWrappers.map((wrapper) => ({
    iterId: parseInt(wrapper.getAttribute('data-iter-id'), 10),
    wrapper,
    rows: Array.from(wrapper.querySelectorAll('.row')),
  }));
};



export const applyStyleToAllInLoop = (
  selectorsArray,
  className,
  htmlString,
  containerElement,
  options = {}
) => {
  console.log("[applyStyleToAllInLoop] ========================================");
  console.log("[applyStyleToAllInLoop] 🚀 START");
  console.log("[applyStyleToAllInLoop] selectorsArray:", JSON.stringify(selectorsArray, null, 2));
  console.log("[applyStyleToAllInLoop] className:", className);
  console.log("[applyStyleToAllInLoop] options:", options);

  const { isCardView = false, selectedText = "", applyToRow = false } = options;

  const selectors = Array.isArray(selectorsArray)
    ? selectorsArray
    : [selectorsArray];
  if (selectors.length === 0) {
    console.log("[applyStyleToAllInLoop] ⚠️ No selectors provided");
    return { success: false, error: "No selectors provided" };
  }

  if (!containerElement) {
    throw new Error("containerElement is required and must be item-ui-screen");
  }
  if (
    !containerElement.classList ||
    !containerElement.classList.contains("item-ui-screen")
  ) {
    throw new Error(
      `containerElement must be item-ui-screen, got: ${containerElement.className || containerElement.tagName}`,
    );
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const container = doc.body;
  let totalElementsModified = 0;

  console.log("[applyStyleToAllInLoop] 📄 Parsed document container:", {
    tag: container?.tagName,
    class: container?.className,
    childrenCount: container?.children?.length,
  });

  try {
    const firstSelector = selectors[0];
    if (
      !firstSelector ||
      !firstSelector.loopSelector ||
      !firstSelector.start ||
      !firstSelector.start.elementPath
    ) {
      console.log("[applyStyleToAllInLoop] ⚠️ Invalid selector, missing loopSelector");
      return { success: false, error: "Invalid loop selector" };
    }

    const loopPath = firstSelector.loopSelector;
    console.log("[applyStyleToAllInLoop] 🔍 loopPath:", loopPath);

    // ------------------------------------------------------------
    // Collect all screens (fallback: wrap contentWraps).
    // ------------------------------------------------------------
    const allScreens = Array.from(container.querySelectorAll(".item-ui-screen"));
    if (allScreens.length === 0) {
      const contentWrapsList = Array.from(container.querySelectorAll(".contentWraps"));
      for (const wrap of contentWrapsList) {
        const wrapper = doc.createElement("div");
        wrapper.className = "item-ui-screen";
        wrapper.setAttribute("data-item-ui-index", "0");
        const parent = wrap.parentNode;
        parent.replaceChild(wrapper, wrap);
        wrapper.appendChild(wrap);
        allScreens.push(wrapper);
      }
    }

    console.log(`[applyStyleToAllInLoop] 📊 Found ${allScreens.length} screens to process`);

    for (let screenIdx = 0; screenIdx < allScreens.length; screenIdx++) {
      const screen = allScreens[screenIdx];
      const screenIndex = screen.getAttribute("data-item-ui-index") || screenIdx;

      console.log(`[applyStyleToAllInLoop] 🖥️ Processing screen ${screenIndex} (${screenIdx + 1}/${allScreens.length})`);

      // ----------------------------------------------------------
      // ✅ CLEAN RESOLUTION: loopPath is screen-rooted. Use it
      //    directly. Verify the result is a .for-loop-started.
      // ----------------------------------------------------------
      let loopContainer = findElementByPath(screen, loopPath);

      if (!loopContainer || !loopContainer.classList.contains("for-loop-started")) {
        console.log(
          `[applyStyleToAllInLoop] ⚠️ Screen ${screenIndex}: direct resolution failed (or not a loop). Trying querySelector fallback.`
        );
        loopContainer = screen.querySelector(".for-loop-started");
      }

      if (!loopContainer) {
        console.log(`[applyStyleToAllInLoop] ⚠️ Screen ${screenIndex} - Could not find loop container, skipping`);
        continue;
      }

      console.log(`[applyStyleToAllInLoop] ✅ Screen ${screenIndex} - Found loop container`);
      console.log(`[applyStyleToAllInLoop] 🔍 data-wrap-iterations:`, loopContainer.getAttribute("data-wrap-iterations"));

      // ----------------------------------------------------------
      // ✅ BRANCH: wrapped vs legacy
      // ----------------------------------------------------------
      const iterationGroups = getIterationGroups(loopContainer);

      if (iterationGroups) {
        console.log(`[applyStyleToAllInLoop] 🆕 Screen ${screenIndex} - WRAPPED loop, ${iterationGroups.length} iterations`);

        for (const selector of selectors) {
          // Support both shapes of start/end.
          const startPath =
            typeof selector.start === "string"
              ? selector.start
              : selector.start?.elementPath || null;
          const endPath =
            typeof selector.end === "string"
              ? selector.end
              : selector.end?.elementPath || null;

          if (!startPath || !endPath) continue;

          // Path inside wrapped loop: 
          //   div:nth-child(I) > div:nth-child(R) [> child ...]
          //     ^ iter wrapper   ^ row
          const cleanParts = startPath.split(" > ");
          const cleanEndParts = endPath.split(" > ");

          let rowIndexInIter = -1;
          let childStartPath = "";
          let childEndPath = "";
          let shape = "unknown";

          // Shape A: first segment is the iteration wrapper.
          if (cleanParts.length >= 2) {
            const probeEl = findElementByPath(loopContainer, cleanParts[0]);
            if (probeEl && probeEl.hasAttribute && probeEl.hasAttribute("data-iter-id")) {
              shape = "A";
              const rowMatch = cleanParts[1].match(/div:nth-child\((\d+)\)/);
              if (rowMatch) {
                rowIndexInIter = parseInt(rowMatch[1], 10) - 1;
                childStartPath = cleanParts.slice(2).join(" > ");
                const endTail = cleanEndParts.slice(2).join(" > ");
                childEndPath = endTail || childStartPath;
              }
            }
          }

          // Shape B: first segment is the row (fallback, e.g. legacy stored selectors).
          if (rowIndexInIter === -1) {
            shape = "B";
            const rowMatch = cleanParts[0]?.match(/div:nth-child\((\d+)\)/);
            if (rowMatch) {
              rowIndexInIter = parseInt(rowMatch[1], 10) - 1;
              childStartPath = cleanParts.slice(1).join(" > ");
              const endTail = cleanEndParts.slice(1).join(" > ");
              childEndPath = endTail || childStartPath;
            }
          }

          // ============================================================
          // ✅ DEV-ONLY GUARD — 1D
          // INVARIANT: every selector arriving at applyStyleToAllInLoop
          //            must be classifiable as Shape A (iter-anchored)
          //            or Shape B (row-anchored) — never "unknown".
          //
          // Reason: an "unknown" shape means the path does not match
          // either of the two sanctioned shapes. The application step
          // will silently skip the selector, and the user will see
          // their style not applied. This almost always means a
          // path-generation bug upstream.
          //
          // Owned by: getRelativePath / getNthChildPath in renderingUtils.js
          //           + createDeepSelectors in PreviewTester.js
          // ============================================================
          if (process.env.NODE_ENV !== "production") {
            if (shape === "unknown") {
              console.warn(
                "[INVARIANT:1D] applyStyleToAllInLoop could not classify path shape.",
                {
                  screenIndex,
                  rawStart: startPath,
                  rawEnd: endPath,
                  cleanParts,
                  cleanEndParts,
                  loopPath,
                  loopContainerClass: loopContainer.className,
                },
              );
            }
          }

          console.log("[applyStyleToAllInLoop] 🧪 PATH DEBUG", {
            rawStart: startPath,
            rawEnd: endPath,
            cleanParts,
            cleanEndParts,
            shape,
            rowIndexInIter,
            childStartPath,
            childEndPath,
            iterationGroupsLength: iterationGroups.length,
          });

          if (rowIndexInIter === -1) {
            console.log(`[applyStyleToAllInLoop] ⚠️ Wrapped loop: cannot resolve row from: ${startPath}`);
            continue;
          }

          const isRowLevel = !childStartPath || childStartPath.trim() === "";

          console.log(
            `[applyStyleToAllInLoop] 📍 Resolved shape=${shape}: row=${rowIndexInIter}, isRowLevel=${isRowLevel}, child="${childStartPath}"`
          );

          for (let g = 0; g < iterationGroups.length; g++) {
            const group = iterationGroups[g];
            const targetRow = group.rows[rowIndexInIter];
            if (!targetRow) {
              console.log(`[applyStyleToAllInLoop] ⚠️ Wrapped loop: no row ${rowIndexInIter} in iter ${g}`);
              continue;
            }

            if (isRowLevel || applyToRow) {
              const existingClass = targetRow.className || "";
              const classes = existingClass.split(" ").filter((c) => c && c !== className);
              classes.push(className);
              targetRow.className = classes.join(" ");
              totalElementsModified++;
              continue;
            }

            const startElement = findElementByPath(targetRow, childStartPath);
            if (startElement) {
              const existingClass = startElement.className || "";
              const classes = existingClass.split(" ").filter((c) => c && c !== className);
              classes.push(className);
              startElement.className = classes.join(" ");
              totalElementsModified++;
            }

            if (childEndPath && childEndPath !== childStartPath) {
              const endElement = findElementByPath(targetRow, childEndPath);
              if (endElement && endElement !== startElement) {
                const existingClass = endElement.className || "";
                const classes = existingClass.split(" ").filter((c) => c && c !== className);
                classes.push(className);
                endElement.className = classes.join(" ");
                totalElementsModified++;
              }
            }
          }
        }

        continue; // wrapped loop done with this screen
      }

      // ----------------------------------------------------------
      // ✅ LEGACY PATH — unchanged behavior.
      // ----------------------------------------------------------
      console.log(`[applyStyleToAllInLoop] 🕰️ Screen ${screenIndex} - LEGACY path`);

      const itemsPerIteration =
        parseInt(loopContainer.getAttribute("data-items-per-iteration")) || 0;
      if (!itemsPerIteration) {
        console.error(`[applyStyleToAllInLoop] ❌ Screen ${screenIndex} - No data-items-per-iteration found`);
        continue;
      }

      const allRowsArray = Array.from(loopContainer.querySelectorAll(".row"));

      for (const selector of selectors) {
        const startPath =
          typeof selector.start === "string"
            ? selector.start
            : selector.start?.elementPath || null;
        const endPath =
          typeof selector.end === "string"
            ? selector.end
            : selector.end?.elementPath || null;

        if (!startPath || !endPath) continue;

        const cleanPathParts = startPath.split(" > ");
        const rowMatch = cleanPathParts[0].match(/div:nth-child\((\d+)\)/);
        if (!rowMatch) {
          console.log(`[applyStyleToAllInLoop] ⚠️ Legacy: cannot parse nth-child from: ${startPath}`);
          continue;
        }

        const targetElement = findElementByPath(loopContainer, startPath);
        if (!targetElement) {
          console.log(`[applyStyleToAllInLoop] ⚠️ Legacy: target element not found: ${startPath}`);
          continue;
        }

        let targetRowIndex = -1;
        for (let i = 0; i < allRowsArray.length; i++) {
          if (allRowsArray[i] === targetElement || allRowsArray[i].contains(targetElement)) {
            targetRowIndex = i;
            break;
          }
        }
        if (targetRowIndex === -1) continue;

        const targetPositionInIteration = targetRowIndex % itemsPerIteration;

        const isRowLevel = (() => {
          if (cleanPathParts.length === 1) return true;
          let sampleRow = null;
          for (let i = 0; i < allRowsArray.length; i++) {
            if (i % itemsPerIteration === targetPositionInIteration) {
              sampleRow = allRowsArray[i];
              break;
            }
          }
          if (!sampleRow) return false;
          const rowChildren = Array.from(sampleRow.children);
          if (rowChildren.length === 0) return false;

          const startParts = startPath.split(" > ");
          const endParts = endPath.split(" > ");
          const startChildMatch = startParts[startParts.length - 1].match(/div:nth-child\((\d+)\)/);
          const endChildMatch = endParts[endParts.length - 1].match(/div:nth-child\((\d+)\)/);
          if (!startChildMatch || !endChildMatch) return false;
          const startChildIndex = parseInt(startChildMatch[1], 10) - 1;
          const endChildIndex = parseInt(endChildMatch[1], 10) - 1;
          return startChildIndex === 0 && endChildIndex === rowChildren.length - 1;
        })();

        for (let i = 0; i < allRowsArray.length; i++) {
          const row = allRowsArray[i];
          if (i % itemsPerIteration !== targetPositionInIteration) continue;

          if (isRowLevel || applyToRow) {
            const existingClass = row.className || "";
            const classes = existingClass.split(" ").filter((c) => c && c !== className);
            classes.push(className);
            row.className = classes.join(" ");
            totalElementsModified++;
          } else {
            const startRemainingPath = cleanPathParts.slice(1).join(" > ");
            const startElement = findElementByPath(row, startRemainingPath);
            if (startElement) {
              const existingClass = startElement.className || "";
              const classes = existingClass.split(" ").filter((c) => c && c !== className);
              classes.push(className);
              startElement.className = classes.join(" ");
              totalElementsModified++;
            }
            if (endPath !== startPath) {
              const endRemainingPath = endPath.split(" > ").slice(1).join(" > ");
              const endElement = findElementByPath(row, endRemainingPath);
              if (endElement && endElement !== startElement) {
                const existingClass = endElement.className || "";
                const classes = existingClass.split(" ").filter((c) => c && c !== className);
                classes.push(className);
                endElement.className = classes.join(" ");
                totalElementsModified++;
              }
            }
          }
        }
      }
    }

    if (totalElementsModified === 0) {
      console.log("[applyStyleToAllInLoop] ⚠️ No elements found to modify");
      return { success: false, error: "No elements found to modify" };
    }

    let serialized = "";
    for (const child of container.children) serialized += child.outerHTML;

    console.log(`[applyStyleToAllInLoop] ✅ Applied class to ${totalElementsModified} elements`);
    return {
      success: true,
      newPreview: serialized,
      elementsModified: totalElementsModified,
    };
  } catch (err) {
    console.error("[applyStyleToAllInLoop] ❌ Error:", err);
    return { success: false, error: err.message };
  }
};

/**
 * Apply the "cards" transformation to a wrapped loop by adding CSS
 * classes to the loop container and each iteration wrapper. The row
 * structure is left intact so style/media/alignment transformations
 * keep working unchanged.
 *
 * Returns { success, newPreview, selectors, elementsModified } on
 * success, or { success: false, error } when the target is not a
 * wrapped loop (caller should fall through to the legacy path).
 */
export const applyCardsToWrappedLoop = (
  htmlString,
  options = {},
  containerElement = null,
) => {
  console.log("[applyCardsToWrappedLoop] 🚀 START");

  const selectors = Array.isArray(options.selectors)
    ? options.selectors
    : options.selector
      ? [options.selector]
      : [];
  if (selectors.length === 0) {
    return { success: false, error: "No selectors provided" };
  }

  const loopSelector = selectors.find((s) => s?.loopSelector)?.loopSelector;
  if (!loopSelector) {
    return { success: false, error: "Not a loop-scoped selection" };
  }

  const scrollMode = options.scrollMode === "horizontal" ? "horizontal" : "vertical";
  const DIR_CLASS = scrollMode === "horizontal"
    ? "previewCardContainerHorizontal"
    : "previewCardContainerVertical";
  const OPPOSITE_DIR_CLASS = scrollMode === "horizontal"
    ? "previewCardContainerVertical"
    : "previewCardContainerHorizontal";

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  const allScreens = Array.from(doc.body.querySelectorAll(".item-ui-screen"));
  if (allScreens.length === 0) {
    const contentWrapsList = Array.from(doc.body.querySelectorAll(".contentWraps"));
    for (const wrap of contentWrapsList) {
      const wrapper = doc.createElement("div");
      wrapper.className = "item-ui-screen";
      wrapper.setAttribute("data-item-ui-index", "0");
      const parent = wrap.parentNode;
      parent.replaceChild(wrapper, wrap);
      wrapper.appendChild(wrap);
      allScreens.push(wrapper);
    }
  }
  if (allScreens.length === 0) {
    return { success: false, error: "No screens in preview" };
  }

  let totalWrappersClassed = 0;
  let totalContainersClassed = 0;
  let totalDirectionChanges = 0;
  let screensTouched = 0;
  let firstFailureReason = null;

  for (let sIdx = 0; sIdx < allScreens.length; sIdx++) {
    const screen = allScreens[sIdx];

    // ----------------------------------------------------------
    // ✅ CLEAN RESOLUTION.
    // ----------------------------------------------------------
    let loopContainer = findElementByPath(screen, loopSelector);
    if (!loopContainer || !loopContainer.classList.contains("for-loop-started")) {
      console.log(`[applyCardsToWrappedLoop] ⚠️ Screen ${sIdx}: direct resolution failed, trying querySelector`);
      loopContainer = screen.querySelector(".for-loop-started");
    }
    if (!loopContainer) {
      console.log(`[applyCardsToWrappedLoop] ⏭️ Screen ${sIdx}: loop container not found, skipping`);
      if (!firstFailureReason) firstFailureReason = "Loop container not found in one or more screens";
      continue;
    }

    // ============================================================
    // ✅ DEV-ONLY GUARD — 1F
    // INVARIANT: applyCardsToWrappedLoop MUST only operate on a
    //            loop container that has data-wrap-iterations="true".
    //
    // Reason: the card-class-adder logic relies on the wrapped
    // iteration structure (each [data-iter-id] div becomes a card).
    // On a legacy (unwrapped) loop this function would either no-op
    // or misclassify the row containers. The caller is expected to
    // fall through to the legacy path when this guard fires.
    //
    // Owned by: enhanceTemplateWithLoopWrappers in templateEnhancer.js
    // ============================================================
    if (process.env.NODE_ENV !== "production") {
      const wrapAttr = loopContainer.getAttribute("data-wrap-iterations");
      if (wrapAttr !== "true") {
        console.warn(
          "[INVARIANT:1F] applyCardsToWrappedLoop received a non-wrapped loop container.",
          {
            screenIndex: sIdx,
            loopClass: loopContainer.className,
            dataWrapIterations: wrapAttr,
            loopSelector,
          },
        );
      }
    }

    if (loopContainer.getAttribute("data-wrap-iterations") !== "true") {
      console.log(`[applyCardsToWrappedLoop] ⏭️ Screen ${sIdx}: not a wrapped loop, skipping`);
      if (!firstFailureReason) firstFailureReason = "Not a wrapped loop";
      continue;
    }

    const iterWrappers = Array.from(loopContainer.children).filter(
      (el) => el.hasAttribute && el.hasAttribute("data-iter-id"),
    );
    if (iterWrappers.length === 0) {
      console.log(`[applyCardsToWrappedLoop] ⏭️ Screen ${sIdx}: no iteration wrappers, skipping`);
      if (!firstFailureReason) firstFailureReason = "No iteration wrappers";
      continue;
    }

    let touched = false;

    if (!loopContainer.classList.contains("previewCardContainer")) {
      loopContainer.classList.add("previewCardContainer");
      totalContainersClassed++;
      touched = true;
    }

    for (const wrapper of iterWrappers) {
      if (!wrapper.classList.contains("previewCard")) {
        wrapper.classList.add("previewCard");
        totalWrappersClassed++;
        touched = true;
      }
    }

    const hasCorrectDir = loopContainer.classList.contains(DIR_CLASS);
    const hasOppositeDir = loopContainer.classList.contains(OPPOSITE_DIR_CLASS);

    if (!hasCorrectDir) {
      loopContainer.classList.add(DIR_CLASS);
      totalDirectionChanges++;
      touched = true;
    }
    if (hasOppositeDir) {
      loopContainer.classList.remove(OPPOSITE_DIR_CLASS);
      totalDirectionChanges++;
      touched = true;
    }

    if (touched) screensTouched++;
  }

  if (screensTouched === 0) {
    console.log("[applyCardsToWrappedLoop] ⚠️ No screens were modified");
    return {
      success: false,
      error: firstFailureReason || "No screens were modified",
    };
  }

  let serialized = "";
  for (const child of doc.body.children) serialized += child.outerHTML;

  console.log(
    `[applyCardsToWrappedLoop] ✅ Applied card classes across ${screensTouched} screen(s): ` +
    `${totalContainersClassed} container(s), ${totalWrappersClassed} iteration(s), ` +
    `${totalDirectionChanges} direction change(s) → ${scrollMode}`,
  );

  return {
    success: true,
    newPreview: serialized,
    selectors,
    elementsModified:
      totalContainersClassed + totalWrappersClassed + totalDirectionChanges,
  };
};

export const applyTransformationLogic = (
  selectedText,
  operation,
  isStructural,
  renderedPreview,
  template,
  parsedSample,
  selectedContainer,
  selectedRange,
  transformationRules = [],
  isSampleArray = false,
  dataType = "text",
  arrayLayoutDirection = "vertical",
  layoutDirection = "vertical",
  options = {},
) => {
  console.log("[applyTransformationLogic] 📥 Full Input:", {
    selectedText,
    operation,
    isStructural,
    renderedPreviewLength: renderedPreview?.length || 0,
    options,
  });

  if (!selectedContainer) {
    throw new Error("selectedContainer is required and must be item-ui-screen");
  }
  if (
    !selectedContainer.classList ||
    !selectedContainer.classList.contains("item-ui-screen")
  ) {
    throw new Error(
      `selectedContainer must be item-ui-screen, got: ${selectedContainer.className || selectedContainer.tagName}`,
    );
  }
  if (!selectedText) return { success: false, error: "No text selected" };
  if (!renderedPreview) return { success: false, error: "No preview content" };
  if (!renderedPreview.includes("<div")) {
    return { success: false, error: "Preview does not contain HTML tokens" };
  }

  // ============================================================
  // STRUCTURAL
  // ============================================================
  if (isStructural) {
    if (operation === "cards") {
      const cardsResult = applyCardsToWrappedLoop(
        renderedPreview,
        options,
        selectedContainer,
      );

      if (cardsResult.success) {
        const step = createMinimalTransformationStep(
          selectedText,
          "cards",
          cardsResult.newPreview,
          null,
          "structural",
          template,
          parsedSample,
          selectedContainer,
          selectedRange,
          {
            selectors: cardsResult.selectors,
            fields: [],
          },
        );
        return {
          success: true,
          newPreview: cardsResult.newPreview,
          step,
          elementsModified: cardsResult.elementsModified,
        };
      }
    }

    const parsedData = parseRowsToData(selectedText);
    if (!parsedData || parsedData.length === 0) {
      const fallbackData = parseSelectedText(selectedText);
      if (!fallbackData || fallbackData.length === 0) {
        return { success: false, error: "Could not parse data from selected text" };
      }
      const transformedResult = generateTransformedContent(fallbackData, operation, options);
      return applyTransformationToPreview(
        transformedResult, renderedPreview, selectedText, operation,
        fallbackData, template, parsedSample, options,
      );
    }
    const transformedResult = generateTransformedContent(parsedData, operation, options);
    if (!transformedResult) {
      return { success: false, error: `Unknown operation: ${operation}` };
    }
    return applyTransformationToPreview(
      transformedResult, renderedPreview, selectedText, operation,
      parsedData, template, parsedSample, options,
    );
  }

  // ============================================================
  // STYLE
  // ============================================================
  let operations = [];
  if (options.operations && Array.isArray(options.operations) && options.operations.length > 0) {
    operations = options.operations;
  } else if (operation) {
    operations = [operation];
  } else {
    operations = ["highlight"];
  }

  const styleMeta = options.style_meta || {
    full_row: options.isFullRow || options.fullRow || false,
    alignment: options.alignment || "left",
  };

  const alignment = styleMeta.alignment || "left";

  const isCardView =
    renderedPreview.includes("previewCard") ||
    renderedPreview.includes("previewCardContainer");

  let selectors = [];
  if (options.selectors && Array.isArray(options.selectors) && options.selectors.length > 0) {
    selectors = options.selectors;
  } else if (options.selector) {
    selectors = [options.selector];
  } else {
    const singleSelector = generatePositionSelector(
      selectedText, selectedContainer, selectedRange, 20,
    );
    if (singleSelector && singleSelector.start && singleSelector.start.elementPath) {
      selectors = [singleSelector];
    }
  }

  selectors = selectors.filter(
    (s) => s && s.start && s.start.elementPath && s.end && s.end.elementPath,
  );
  if (selectors.length === 0) {
    return { success: false, error: "No valid selector could be generated" };
  }

  const hasLoopSelector = selectors.some(
    (s) => s.loopSelector && s.loopSelector.length > 0,
  );

  // ============================================================
  // INSIDE LOOP
  // ============================================================
  if (hasLoopSelector) {
    console.log("[applyTransformationLogic] 🔄 INSIDE LOOP");

    const validLoopSelectors = selectors.filter(
      (s) => s.loopSelector && s.loopSelector.length > 0,
    );

    if (validLoopSelectors.length > 0) {
      let currentPreview = renderedPreview;
      let totalElementsModified = 0;

      if (alignment) {
        const alignmentClass = `text-${alignment}`;
        const alignmentResult = applyStyleToAllInLoop(
          validLoopSelectors,
          alignmentClass,
          currentPreview,
          selectedContainer,
          { isCardView, selectedText, applyToRow: true },
        );
        if (alignmentResult.success) {
          currentPreview = alignmentResult.newPreview;
          totalElementsModified += alignmentResult.elementsModified || 0;
        }
      }

      for (const op of operations) {
        const opClassName = CLASS_MAP[op];
        if (!opClassName) continue;

        const loopResult = applyStyleToAllInLoop(
          validLoopSelectors,
          opClassName,
          currentPreview,
          selectedContainer,
          { isCardView, selectedText, applyToRow: false },
        );

        if (loopResult.success) {
          currentPreview = loopResult.newPreview;
          totalElementsModified += loopResult.elementsModified || 0;
        }
      }

      if (totalElementsModified > 0) {
        const step = createMinimalTransformationStep(
          selectedText, operations[0], currentPreview, null, "style",
          template, parsedSample, selectedContainer, selectedRange,
          {
            selectors: validLoopSelectors,
            operations,
            style_meta: styleMeta,
          },
        );
        return {
          success: true,
          newPreview: currentPreview,
          step,
          elementsModified: totalElementsModified,
        };
      }
    }
  }

  // ============================================================
  // OUTSIDE LOOP
  // ============================================================
  console.log("[applyTransformationLogic] 📍 OUTSIDE LOOP");

  const parser = new DOMParser();
  const doc = parser.parseFromString(renderedPreview, "text/html");

  let screens = doc.querySelectorAll(".item-ui-screen");
  if (screens.length === 0) {
    const contentWrapsList = doc.querySelectorAll(".contentWraps");
    const wrapped = [];
    for (const wrap of contentWrapsList) {
      const wrapper = doc.createElement("div");
      wrapper.className = "item-ui-screen";
      wrapper.setAttribute("data-item-ui-index", "0");
      const parent = wrap.parentNode;
      parent.replaceChild(wrapper, wrap);
      wrapper.appendChild(wrap);
      wrapped.push(wrapper);
    }
    screens = wrapped;
  }

  if (screens.length === 0) {
    return { success: false, error: "No screens found" };
  }

  let totalElementsModified = 0;

  // ============================================================
  // Separate tracking:
  //   allAffectedRows     → any row touched (alignment / bookkeeping)
  //   rowsToPaintDirectly → rows selected as a whole (style ops)
  //   allAffectedElements → individual child elements (style ops)
  // ============================================================
  const allAffectedRows = new Set();
  const rowsToPaintDirectly = new Set();
  const allAffectedElements = [];

  // ============================================================
  // ✅ TOLERANT PATH RESOLUTION
  //
  // All newly emitted paths are `.item-ui-screen`-rooted and match
  // the string DOM 1:1 (React wrapper is skipped at generation time).
  //
  // A single fallback — stripping the leading segment — remains
  // for backward compatibility with any old DB-stored paths.
  // ============================================================
  const resolvePathTolerant = (screen, path) => {
    if (!screen || !path) return null;

    let el = findElementByPath(screen, path);
    if (el) {
      return { element: el, container: screen, resolvedPath: path };
    }

    // Legacy fallback: strip the leading segment and retry.
    const parts = path.split(" > ");
    if (parts.length > 1) {
      const stripped = parts.slice(1).join(" > ");
      el = findElementByPath(screen, stripped);
      if (el) {
        return { element: el, container: screen, resolvedPath: stripped };
      }
    }

    return null;
  };

  // ============================================================
  // MAIN LOOP OVER SELECTORS
  // ============================================================
  for (const selector of selectors) {
    const startPath =
      typeof selector.start === "string"
        ? selector.start
        : selector.start?.elementPath || null;
    const endPath =
      typeof selector.end === "string"
        ? selector.end
        : selector.end?.elementPath || null;
    if (!startPath || !endPath) continue;

    for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
      const screen = screens[screenIdx];

      const resolvedStart = resolvePathTolerant(screen, startPath);
      if (!resolvedStart) {
        console.log(
          `[applyTransformationLogic] ⚠️ Could not resolve startPath on screen ${screenIdx}:`,
          startPath,
        );
        continue;
      }

      const { element: startElement } = resolvedStart;

      let endElement = null;
      if (endPath === startPath) {
        endElement = startElement;
      } else {
        const resolvedEnd = resolvePathTolerant(screen, endPath);
        endElement = resolvedEnd ? resolvedEnd.element : startElement;
      }
      if (!endElement) endElement = startElement;

      const startIsRow =
        startElement.classList && startElement.classList.contains("row");
      const endIsRow =
        endElement.classList && endElement.classList.contains("row");

      const isSingleRowSelection =
        startElement === endElement && startIsRow && endIsRow;

      // ✅ Full-row selection: paint the row div itself.
      if (isSingleRowSelection) {
        console.log(
          "[applyTransformationLogic] ✅ Row-level selection: painting row directly",
          {
            rowClass: startElement.className,
            text: (startElement.textContent || "").trim().slice(0, 40),
          },
        );
        allAffectedRows.add(startElement);
        rowsToPaintDirectly.add(startElement);
        continue;
      }

      // ----------------------------------------------------------
      // Partial / multi-element selection: walk the subtree of
      // `.item-ui-screen` and collect every element between start
      // and end (inclusive).
      // ----------------------------------------------------------
      const elementsBetween = getElementsBetween(
        startElement,
        endElement,
        screen,
      );

      for (const element of elementsBetween) {
        if (element === screen) continue;
        if (element.classList && element.classList.contains("empty-line")) continue;
        if (element.hasAttribute && element.hasAttribute("data-iter-id")) continue;

        allAffectedElements.push(element);
        const row = element.closest?.(".row");
        if (row) allAffectedRows.add(row);
      }
    }
  }

  // ============================================================
  // APPLY ALIGNMENT (to every row we touched)
  // ============================================================
  if (alignment && allAffectedRows.size > 0) {
    const alignmentClass = `text-${alignment}`;
    for (const row of allAffectedRows) {
      const existingClass = row.className || "";
      const classes = existingClass
        .split(" ")
        .filter((c) => c && !c.startsWith("text-"));
      if (!classes.includes(alignmentClass)) {
        classes.push(alignmentClass);
        row.className = classes.join(" ");
        totalElementsModified++;
      }
    }
  }

  // ============================================================
  // APPLY STYLE OPS
  // ============================================================
  for (const op of operations) {
    const opClassName = CLASS_MAP[op];
    if (!opClassName) continue;

    // 1. Full-row selections → paint the row div.
    for (const row of rowsToPaintDirectly) {
      const existingClass = row.className || "";
      const classes = existingClass
        .split(" ")
        .filter((c) => c && c !== opClassName);
      classes.push(opClassName);
      row.className = classes.join(" ");
      totalElementsModified++;
    }

    // 2. Partial selections → paint child elements.
    if (allAffectedElements.length > 0) {
      const hasChildElements = allAffectedElements.some(
        (el) => !el.classList?.contains("row"),
      );
      const hasRowElements = allAffectedElements.some(
        (el) => el.classList?.contains("row"),
      );

      if (hasRowElements && !hasChildElements && allAffectedRows.size > 0) {
        // Only whole rows were collected: paint their children.
        for (const row of allAffectedRows) {
          for (const child of row.children) {
            if (child.classList && child.classList.contains("empty-line")) continue;
            const existingClass = child.className || "";
            const classes = existingClass
              .split(" ")
              .filter((c) => c && c !== opClassName);
            classes.push(opClassName);
            child.className = classes.join(" ");
            totalElementsModified++;
          }
        }
      } else {
        // Specific child elements were collected: paint them directly.
        for (const element of allAffectedElements) {
          if (element.classList && element.classList.contains("empty-line")) continue;
          if (element.classList && element.classList.contains("row")) continue;
          const existingClass = element.className || "";
          const classes = existingClass
            .split(" ")
            .filter((c) => c && c !== opClassName);
          classes.push(opClassName);
          element.className = classes.join(" ");
          totalElementsModified++;
        }
      }
    }
  }

  if (totalElementsModified === 0) {
    return { success: false, error: "No changes made" };
  }

  let serialized = "";
  for (const child of doc.body.children) serialized += child.outerHTML;

  const step = createMinimalTransformationStep(
    selectedText, operations[0], serialized, null, "style",
    template, parsedSample, selectedContainer, selectedRange,
    { selectors, operations, style_meta: styleMeta },
  );

  return {
    success: true,
    newPreview: serialized,
    step,
    elementsModified: totalElementsModified,
  };
};

// ============================================================
// HELPER: GENERATE TRANSFORMED CONTENT
// ============================================================

// PreviewTester/utils/renderingUtils.js

const generateTransformedContent = (data, operation, options = {}) => {
  console.log("[generateTransformedContent] operation:", operation);
  console.log("[generateTransformedContent] options:", options);

  const { scrollMode = 'vertical' } = options;

  switch (operation) {
    case "table":
      return generateTableHTML(data);
    case "cards":
      return generateCardHTML(data, scrollMode);
    case "json":
      return JSON.stringify(data, null, 2);
    case "list":
      return generateListHTML(data);
    default:
      return null;
  }
};
// ============================================================
// HELPER: APPLY TRANSFORMATION TO PREVIEW
// ============================================================

/**
 * Apply transformation to preview
 * UPDATED: Passes scrollMode to generateCardHTML
 */
const applyTransformationToPreview = (
  transformedResult,
  renderedPreview,
  selectedText,
  operation,
  parsedData,
  template,
  parsedSample,
  options,
) => {
  const scrollMode = options.scrollMode || 'vertical';

  console.log("[applyTransformationToPreview] START", scrollMode);

  let newPreview = renderedPreview;
  let transformationApplied = false;

  // Find for-loop-started elements (tag agnostic - look for class)
  const forLoopMatches = [];
  let searchIndex = 0;
  const openTag = '<div class="for-loop-started"';

  while (true) {
    const startIdx = renderedPreview.indexOf(openTag, searchIndex);
    if (startIdx === -1) break;

    const openTagEnd = renderedPreview.indexOf(">", startIdx);
    if (openTagEnd === -1) break;

    let depth = 1;
    let pos = openTagEnd + 1;
    let found = false;
    let endIdx = -1;

    while (pos < renderedPreview.length) {
      const nextOpen = renderedPreview.indexOf("<div", pos);
      const nextClose = renderedPreview.indexOf("</div>", pos);

      if (nextClose === -1) break;

      if (nextOpen !== -1 && nextOpen < nextClose) {
        depth++;
        pos = nextOpen + 4;
      } else {
        depth--;
        pos = nextClose + 6;

        if (depth === 0) {
          endIdx = nextClose + 6;
          found = true;
          break;
        }
      }
    }

    if (!found || endIdx === -1) {
      console.log("[applyTransformationToPreview] ⚠️ Could not find matching closing tag for for-loop-started");
      searchIndex = startIdx + 1;
      continue;
    }

    const fullMatch = renderedPreview.substring(startIdx, endIdx);
    const content = renderedPreview.substring(openTagEnd + 1, endIdx - 6);

    const loopTypeMatch = renderedPreview
      .substring(startIdx, openTagEnd)
      .match(/data-loop-type="([^"]+)"/);
    const loopType = loopTypeMatch ? loopTypeMatch[1] : "unknown";

    const plainTextContent = content.replace(/<[^>]*>/g, "").trim();
    const spanData = parseRowsToData(plainTextContent);

    forLoopMatches.push({
      fullMatch: fullMatch,
      content: content,
      plainTextContent: plainTextContent,
      start: startIdx,
      end: endIdx,
      openTag: renderedPreview.substring(startIdx, openTagEnd + 1),
      spanData: spanData,
      spanIndex: forLoopMatches.length,
      loopType: loopType,
    });

    searchIndex = endIdx;
  }

  if (forLoopMatches.length > 0) {
    let newPreviewParts = [];
    let lastEnd = 0;

    for (let i = 0; i < forLoopMatches.length; i++) {
      const loopMatch = forLoopMatches[i];

      const shouldTransform =
        loopMatch.content.includes(selectedText) ||
        selectedText.includes(loopMatch.content) ||
        (loopMatch.content.trim().length > 0 && selectedText.trim().length > 0);

      if (shouldTransform) {
        let spanTransformedResult;

        if (loopMatch.spanData && loopMatch.spanData.length > 0) {
          switch (operation) {
            case "table":
              spanTransformedResult = generateTableHTML(loopMatch.spanData);
              break;
            case "cards":
              spanTransformedResult = generateCardHTML(loopMatch.spanData, scrollMode);
              break;
            case "json":
              spanTransformedResult = JSON.stringify(loopMatch.spanData, null, 2);
              break;
            case "list":
              spanTransformedResult = generateListHTML(loopMatch.spanData);
              break;
            default:
              spanTransformedResult = transformedResult;
          }
        } else {
          spanTransformedResult = transformedResult;
        }

        newPreviewParts.push(renderedPreview.substring(lastEnd, loopMatch.start));

        const openingTag = loopMatch.openTag;
        const newFullMatch = `${openingTag}${spanTransformedResult}</div>`;
        newPreviewParts.push(newFullMatch);

        lastEnd = loopMatch.end;
        transformationApplied = true;
      } else {
        newPreviewParts.push(renderedPreview.substring(lastEnd, loopMatch.end));
        lastEnd = loopMatch.end;
      }
    }

    newPreviewParts.push(renderedPreview.substring(lastEnd));
    newPreview = newPreviewParts.join("");
  }

  // Direct replacement fallback
  if (!transformationApplied) {
    const escapedSelectedText = escapeRegExp(selectedText);
    const directPattern = new RegExp(escapedSelectedText, "g");

    if (directPattern.test(renderedPreview)) {
      newPreview = renderedPreview.replace(directPattern, transformedResult);
      transformationApplied = true;
    }
  }

  if (!transformationApplied) {
    newPreview = renderedPreview + "\n" + transformedResult;
    transformationApplied = true;
  }

  let selectorsToSave = [];

  if (
    options.selectors &&
    Array.isArray(options.selectors) &&
    options.selectors.length > 0
  ) {
    selectorsToSave = options.selectors;
  } else if (options.selector) {
    selectorsToSave = [options.selector];
  } else {
    const singleSelector = generatePositionSelector(selectedText, null, null, 23);
    if (singleSelector) selectorsToSave = [singleSelector];
  }

  if (selectorsToSave.length === 0) {
    selectorsToSave = [
      {
        type: "node_selector",
        containerSelector: ".item-ui-screen",
        start: { elementPath: "", nodeType: 1 },
        end: { elementPath: "", nodeType: 1 },
      },
    ];
  }

  const step = createMinimalTransformationStep(
    selectedText,
    operation,
    transformedResult,
    parsedData,
    "structural",
    template,
    parsedSample,
    null,
    null,
    { selectors: selectorsToSave },
  );

  console.log("[applyTransformationToPreview] ✅ Transformation applied");
  return { success: true, newPreview, step };
};

// ============================================================
// GET ELEMENTS BETWEEN
// ============================================================

// ============================================================
// GET ELEMENTS BETWEEN
// ============================================================

export const getElementsBetween = (startElement, endElement, container) => {
  console.log("[getElementsBetween] START");

  if (!startElement || !endElement || !container) {
    console.warn("Missing elements for getElementsBetween");
    return [];
  }

  const allElements = [];
  const collect = (node) => {
    for (const child of node.children) {
      allElements.push(child);
      collect(child);
    }
  };
  collect(container);

  let startIndex = -1;
  let endIndex = -1;

  for (let i = 0; i < allElements.length; i++) {
    if (allElements[i] === startElement) startIndex = i;
    if (allElements[i] === endElement) endIndex = i;
    if (startIndex !== -1 && endIndex !== -1) break;
  }

  if (startIndex === -1 || endIndex === -1) {
    console.warn("Could not find start or end element in DOM tree");
    return [];
  }

  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  const result = allElements.slice(minIndex, maxIndex + 1);

  console.log(`[getElementsBetween] Found ${result.length} elements`);
  return result;
};